import React, { useState, useEffect, useRef } from 'react';
import chatbotService from '../services/chatbotService';
import './Chatbot.css';

const Chatbot = ({ userId }) => {
    // States for chat window
    const [messages, setMessages] = useState([
        { role: 'assistant', content: "Hello! I'm Echo, your English tutor. Choose a topic or create a new one to start chatting!" }
    ]);
    const [input, setInput] = useState('');
    const [topic, setTopic] = useState('General Conversation');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // States for conversation history
    const [conversations, setConversations] = useState([]);
    const [selectedConversationId, setSelectedConversationId] = useState(null);
    const [newTopic, setNewTopic] = useState(''); // State for the new topic input

    const messagesEndRef = useRef(null);

    // --- DATA FETCHING ---
    const fetchConversations = async () => {
        if (!userId) return;
        try {
            const convs = await chatbotService.getConversations();
            setConversations(convs);
        } catch (err) {
            console.error('Failed to load conversation history:', err);
            setError('Could not load conversation history. Please try refreshing the page.');
        }
    };

    // --- EFFECTS ---
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        fetchConversations();
    }, [userId]);

    // --- HANDLERS ---
    const handleSelectConversation = async (conversation) => {
        if (isLoading || selectedConversationId === conversation.id) return;

        setIsLoading(true);
        setError(null);
        try {
            const fetchedMessages = await chatbotService.getConversationMessages(conversation.id);
            // If conversation is new and empty, show a welcome message
            if (fetchedMessages.length === 0) {
                setMessages([{ role: 'assistant', content: `This is a new chat about "${conversation.topic}". You can start the conversation!` }]);
            } else {
                setMessages(fetchedMessages);
            }
            setSelectedConversationId(conversation.id);
            setTopic(conversation.topic);
        } catch (err) {
            setError('Could not load conversation messages.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewChat = () => {
        setSelectedConversationId(null);
        setTopic('General Conversation'); // Reset to a generic topic
        setMessages([
            { role: 'assistant', content: "Hello! I'm Echo, your English tutor. Create a new topic to start chatting!" }
        ]);
        setError(null);
        setInput('');
    };

    const handleCreateConversation = async (e) => {
        e.preventDefault();
        if (!newTopic.trim() || isLoading) return;

        setIsLoading(true);
        setError(null);
        try {
            const newConversation = await chatbotService.createConversation(newTopic);
            setNewTopic('');

            setMessages([{ role: 'assistant', content: `Let's talk about "${newConversation.topic}"! You can start.` }]);
            setSelectedConversationId(newConversation.id);
            setTopic(newConversation.topic);

            await fetchConversations();

        } catch (err) {
            setError('Could not create new topic.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading || !selectedConversationId) {
            if (!selectedConversationId) {
                setError("Please select or create a conversation topic first.");
            }
            return;
        }

        const userMessage = { role: 'user', content: input };
        setMessages(prev => [...prev, userMessage]);
        const currentInput = input;
        setInput('');
        setIsLoading(true);
        setError(null);

        try {
            const payload = {
                userId,
                topic,
                message: currentInput,
                conversationId: selectedConversationId,
            };

            // Use streaming message instead of regular message
            let streamingContent = '';
            let assistantMessage = { role: 'assistant', content: '' };
            setMessages(prev => [...prev, assistantMessage]);
            
            const data = await chatbotService.sendStreamingMessage(payload, (chunk) => {
                // Accumulate content in local variable to avoid duplication
                streamingContent += chunk;
                
                // Update the assistant message with accumulated content
                setMessages(prev => {
                    const newMessages = [...prev];
                    const lastMessage = newMessages[newMessages.length - 1];
                    if (lastMessage.role === 'assistant') {
                        lastMessage.content = streamingContent;
                    }
                    return [...newMessages];
                });
            });

            // If the conversation ID changed (which it shouldn't in this flow, but as a safeguard)
            if (data.conversationId && data.conversationId !== selectedConversationId) {
                setSelectedConversationId(data.conversationId);
                await fetchConversations();
            }

        } catch (error) {
            const errorMessageText = error.message || 'An unknown error occurred.';
            const errorMessage = { role: 'assistant', content: `Sorry, an error occurred: ${errorMessageText}` };
            setMessages(prev => [...prev, errorMessage]);
            setError(errorMessageText);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="chatbot-container-flex">
            <div className="conversation-history">
                <button onClick={handleNewChat} className="new-chat-btn">+ Đoạn chat mới</button>

                <form onSubmit={handleCreateConversation} className="new-topic-form">
                    <input
                        type="text"
                        className="new-topic-input"
                        value={newTopic}
                        onChange={(e) => setNewTopic(e.target.value)}
                        placeholder="Tạo chủ đề mới..."
                        disabled={isLoading}
                    />
                    <button type="submit" className="new-topic-btn" disabled={isLoading || !newTopic.trim()}>
                        Tạo mới
                    </button>
                </form>

                <ul className="conversation-list">
                    {conversations.map(conv => (
                        <li
                            key={conv.id}
                            className={`conversation-item ${conv.id === selectedConversationId ? 'active' : ''}`}
                            onClick={() => handleSelectConversation(conv)}
                        >
                            <p className="conversation-topic">{conv.topic}</p>
                            <p className="conversation-date">{new Date(conv.updatedAt).toLocaleDateString()}</p>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="chat-window">
                <div className="chatbot-header">
                    <h2>Practice English with Echo</h2>
                    <div className="topic-selector">
                        <label htmlFor="topic-display">Topic:</label>
                        <input
                            type="text"
                            id="topic-display"
                            value={topic}
                            readOnly
                            className="topic-display-input"
                        />
                    </div>
                </div>
                <div className="chatbot-messages">
                    {messages.map((msg, index) => (
                        <div key={msg.id || index} className={`message ${msg.role}`}>
                            <p>{msg.content}</p>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="message assistant loading">
                            <p><span></span><span></span><span></span></p>
                        </div>
                    )}
                    {error && !messages.some(m => m.content.includes(error)) && (
                        <div className="message assistant error-bubble"><p>{error}</p></div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
                <form className="chatbot-input" onSubmit={handleSubmit}>
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type your message here..."
                        autoComplete="off"
                        disabled={isLoading || !selectedConversationId}
                    />
                    <button type="submit" disabled={isLoading || !selectedConversationId}>Send</button>
                </form>
            </div>
        </div>
    );
};

export default Chatbot;
